import {
    createContext,
    PropsWithChildren,
    useContext,
    useEffect,
    useMemo,
    useReducer,
    useState,
} from 'react'
import { PGPlusStepAndModalReducer } from '@/src/common/reducers/PGPlusStepAndModalReducer'
import { OPEN_GIFT_STEP } from '@/src/modules/pg-plus/redemption/constants'
import { useUser } from '@/src/modules/auth/context/AuthProvider'
import { IRedeemState } from '@/src/common/reducers/types'
import {
    BRANDED_EGIFT,
    CARD_LINK,
    CHANGE_BRAND,
    PLASTIC_REDEMPTION,
    UNBRANDED_TO_EGIFT,
    UNBRANDED_TO_LINK,
} from '@/src/modules/pg-plus/constants'
import { getSteps, TStep } from '@/src/common/reducers/getSteps'
import { GET_PG_PLUS_STEPS } from '@/src/modules/build-a-card/constants'
import { useLoginContext } from '@/src/modules/login/LoginContext'
import { determineModalType } from '@/src/modules/pg-plus/redemption/utilities'
import { scrollToError } from '@/src/common/utilities/scrollToError'
import * as yup from 'yup'
import { ValidationError } from 'yup'

const redemptionSchema = yup.object().shape({
    selection: yup
        .string()
        .label('Redemption Method')
        .required('Please choose a redemption method')
        .oneOf(
            [
                BRANDED_EGIFT,
                CHANGE_BRAND,
                PLASTIC_REDEMPTION,
                CARD_LINK,
                UNBRANDED_TO_LINK,
                UNBRANDED_TO_EGIFT,
            ],
            'Please select a valid redemption method'
        ),
})

type TPGPlusStepAndModalState = {
    isLoggedIn: boolean
    giftState: IRedeemState
    dispatch: any
    isBrandSelect: boolean
    isBrandedEGift: boolean
    setIsBrandedEGift: Function
    isPlastic: boolean
    setIsPlastic: Function
    isCardLink: boolean
    setIsCardLink: Function
    isRedeemed: boolean
    setIsRedeemed: Function
    isProcessed: boolean
    setIsProcessed: Function
    setIsBrandSelect: Function
    isRedeemedButNotProcessed: boolean
    giftDetailsCanBeViewed: boolean
    finalizedSteps: TStep[]
    currentStepNumber: number
    selection: string
    setSelection: Function
    numberOfSteps: number
    setNumberOfSteps: Function
    isUnbrandedToLink: boolean
    isUnbrandedToEgift: boolean
    validateSelectionAndContinue: Function
    redemptionError: ValidationError[]
    setRedemptionError: Function
}

export const PGPlusStepAndModalContext =
    createContext<TPGPlusStepAndModalState | null>(null)

export const usePGPlusStepAndModalContext = (): TPGPlusStepAndModalState => {
    const context = useContext(PGPlusStepAndModalContext)

    if (!context) {
        throw new Error(
            'Please use PGPlusStepAndModalContext in parent component'
        )
    }

    return context
}

export const PGPlusStepAndModalProvider = (props: PropsWithChildren) => {
    const { isLoggedIn } = useUser()
    const { setPhoneVerificationText } = useLoginContext()

    const [giftState, dispatch] = useReducer(PGPlusStepAndModalReducer, {
        cardUuid: '',
        brand: '',
        brandSlug: '',
        denomination: 0,
        redemptionSelection: '',
        stepName: OPEN_GIFT_STEP,
        showModal: false,
        modalName: '',
        refetchGift: null,
        isPgPlus: false,
        is24HoursOld: false,
        createdAt: undefined,
    })

    const { stepName = '' } = giftState
    const [selection, setSelection] = useState<string>('')
    const [numberOfSteps, setNumberOfSteps] = useState<number>(3)
    const [isRedeemed, setIsRedeemed] = useState<boolean>(false)
    const [isProcessed, setIsProcessed] = useState<boolean>(false)
    const [redemptionError, setRedemptionError] = useState<ValidationError[]>(
        []
    )

    const [isPlastic, setIsPlastic] = useState<boolean>(
        selection === PLASTIC_REDEMPTION
    )
    const [isBrandSelect, setIsBrandSelect] = useState<boolean>(
        selection === CHANGE_BRAND
    )
    const [isBrandedEGift, setIsBrandedEGift] = useState<boolean>(
        selection === BRANDED_EGIFT
    )
    const [isCardLink, setIsCardLink] = useState<boolean>(
        selection === CARD_LINK
    )

    const isUnbrandedToLink: boolean = selection === UNBRANDED_TO_LINK
    const isUnbrandedToEgift: boolean = selection === UNBRANDED_TO_EGIFT
    const isRedeemedButNotProcessed: boolean =
        isRedeemed && !isProcessed && isLoggedIn
    const giftDetailsCanBeViewed: boolean =
        isRedeemed && isProcessed && isLoggedIn

    // get info for step header
    const { finalizedSteps, currentStepNumber } = getSteps(GET_PG_PLUS_STEPS, {
        isLoggedIn: isLoggedIn,
        isPlastic: isPlastic,
        isBrandSelect: isBrandSelect,
        isRedeemed: isRedeemed,
        giftDetailsCanBeViewed: giftDetailsCanBeViewed,
        currentStep: stepName,
        isUnbrandedToBranded: isUnbrandedToLink || isUnbrandedToEgift,
    })

    useEffect(() => {
        // change button text for phone verification code when in the redemption flow
        setPhoneVerificationText('Verify and Redeem')
    }, [setPhoneVerificationText])

    const validateSelectionAndContinue = async (
        selection: string,
        numberOfSteps: number
    ) => {
        setSelection(selection)
        setNumberOfSteps(numberOfSteps)

        await redemptionSchema
            .validate(
                {
                    selection,
                },
                {
                    abortEarly: false,
                    context: { checkLanguage: true },
                }
            )
            .then(() => {
                setRedemptionError([])
                const requiredModalType = determineModalType(selection)
                let payload: object = {}

                requiredModalType &&
                    dispatch({ type: requiredModalType, payload: payload })
            })
            .catch((err: any) => {
                setRedemptionError(err.inner)
                scrollToError()
            })
    }

    const functions = useMemo(
        () => ({
            setIsPlastic,
            setIsBrandSelect,
            setIsRedeemed,
            setIsProcessed,
            setSelection,
            setNumberOfSteps,
            setIsBrandedEGift,
            setIsCardLink,
            validateSelectionAndContinue,
            setRedemptionError,
            dispatch,
        }),
        []
    )

    const values = useMemo(
        () => ({
            isLoggedIn,
            giftState,
            isBrandSelect,
            isBrandedEGift,
            isPlastic,
            isCardLink,
            isUnbrandedToLink,
            selection,
            isRedeemed,
            isProcessed,
            isRedeemedButNotProcessed,
            giftDetailsCanBeViewed,
            currentStepNumber,
            finalizedSteps,
            numberOfSteps,
            isUnbrandedToEgift,
            redemptionError,
        }),
        [
            isLoggedIn,
            giftState,
            isBrandSelect,
            isBrandedEGift,
            isPlastic,
            isCardLink,
            isUnbrandedToLink,
            selection,
            isRedeemed,
            isProcessed,
            isRedeemedButNotProcessed,
            giftDetailsCanBeViewed,
            currentStepNumber,
            finalizedSteps,
            numberOfSteps,
            isUnbrandedToEgift,
            redemptionError,
        ]
    )

    return (
        <PGPlusStepAndModalContext.Provider value={{ ...values, ...functions }}>
            {props.children}
        </PGPlusStepAndModalContext.Provider>
    )
}
